import React, { forwardRef, useImperativeHandle, useRef } from "react";
import cx from "classnames";

import styles from "./InputCode.module.scss";

export interface InputCodeHandle {
  focus: () => void;
}

export interface InputCodeProps {
  value?: string[];
  length?: number;
  type?: "text" | "password";
  size?: "small" | "large";
  error?: string;
  autoFocus?: boolean;
  className?: string;
  onChange?: (value: string[]) => void;
  onComplete?: (code: string) => void;
}

export const InputCode = forwardRef<InputCodeHandle, InputCodeProps>(
  (
    {
      value,
      length = 4,
      type = "text",
      size = "large",
      error,
      autoFocus,
      className,
      onChange,
      onComplete,
    },
    ref
  ) => {
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
    const values = Array.from({ length }, (_, index) => value?.[index] ?? "");

    const focusInput = (index: number) => inputsRef.current[index]?.focus();

    useImperativeHandle(ref, () => ({ focus: () => focusInput(0) }));

    const update = (next: string[]) => {
      onChange?.(next);

      if (next.every(Boolean)) {
        onComplete?.(next.join(""));
      }
    };

    // The box content is always selected (see selectContent), so the input
    // value is exactly what was typed or pasted, or empty after a delete.
    const handleChange = (index: number, inputValue: string) => {
      const digits = inputValue.replace(/\D/g, "").slice(0, length - index);

      if (inputValue && !digits) {
        return;
      }

      const next = [...values];

      if (digits) {
        digits.split("").forEach((digit, offset) => {
          next[index + offset] = digit;
        });
        focusInput(Math.min(index + digits.length, length - 1));
      } else {
        next[index] = "";
      }

      update(next);
    };

    const handleKeyDown = (
      index: number,
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === "Backspace" && !values[index] && index > 0) {
        event.preventDefault();
        update(values.map((digit, i) => (i === index - 1 ? "" : digit)));
        focusInput(index - 1);
      } else if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      } else if (event.key === "ArrowRight" && index < length - 1) {
        event.preventDefault();
        focusInput(index + 1);
      }
    };

    const selectContent = (event: React.SyntheticEvent<HTMLInputElement>) =>
      event.currentTarget.select();

    return (
      <div className={cx(styles.container, styles[size], className)}>
        <div className={styles.inputs}>
          {values.map((digit, index) => (
            <input
              key={index}
              ref={(input) => {
                inputsRef.current[index] = input;
              }}
              className={cx(styles.input, { [styles.inputError]: error })}
              type={type}
              inputMode="numeric"
              autoComplete="off"
              placeholder={type === "password" ? "*" : undefined}
              autoFocus={autoFocus && index === 0}
              value={digit}
              onChange={(event) => handleChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onFocus={selectContent}
              onClick={selectContent}
            />
          ))}
        </div>
        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
  }
);

InputCode.displayName = "InputCode";
